import { useState } from 'react';
import { Plus, CreditCard as Edit2, Trash2, X, ChevronDown, ChevronUp, TrendingUp, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAgriLivestockHerds, useAgriLivestockSales, useAgriLivestockCharges } from '../../hooks/useAgriculture';
import { LIVESTOCK_TYPE_LABELS, formatEur, formatNum, MONTH_LABELS, distributeAnnualAmount } from '../../utils/agriEngine';
import type { AgriLivestockHerd, AgriLivestockSale, AgriLivestockCharge, LivestockType, MonthlyPattern } from '../../utils/agriEngine';

interface Props { year: number; }

function HerdFormModal({ herd, onClose, onSaved }: { herd?: AgriLivestockHerd | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: herd?.name || '',
    livestock_type: (herd?.livestock_type || 'bovin_viande') as LivestockType,
    cows_count: String(herd?.cows_count || 0),
    heifers_count: String(herd?.heifers_count || 0),
    calves_count: String(herd?.calves_count || 0),
    other_count: String(herd?.other_count || 0),
    notes: herd?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.name) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    const cows = parseInt(form.cows_count) || 0;
    const heifers = parseInt(form.heifers_count) || 0;
    const calves = parseInt(form.calves_count) || 0;
    const other = parseInt(form.other_count) || 0;
    const payload = { user_id: user.id, name: form.name, livestock_type: form.livestock_type, cows_count: cows, heifers_count: heifers, calves_count: calves, other_count: other, total_animals: cows + heifers + calves + other, notes: form.notes || null };
    const { error } = herd ? await supabase.from('agri_livestock_herds').update(payload).eq('id', herd.id) : await supabase.from('agri_livestock_herds').insert(payload);
    setSaving(false);
    if (!error) onSaved();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{herd ? 'Modifier le troupeau' : 'Nouveau troupeau'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du troupeau *</label>
            <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Troupeau Bovin Viande"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type d'élevage</label>
            <select value={form.livestock_type} onChange={e => set('livestock_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
              {Object.entries(LIVESTOCK_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['cows_count', 'Vaches / Truies / Brebis'], ['heifers_count', 'Génisses / Cochettes'], ['calves_count', 'Veaux / Porcelets / Agneaux'], ['other_count', 'Autres']].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                <input type="number" min="0" value={form[k as keyof typeof form]} onChange={e => set(k as keyof typeof form, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm resize-none" />
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HerdDetail({ herd, year, onClose }: { herd: AgriLivestockHerd; year: number; onClose: () => void }) {
  const { sales, refresh: refreshSales } = useAgriLivestockSales(herd.id, year);
  const { charges, refresh: refreshCharges } = useAgriLivestockCharges(herd.id, year);
  const [activeTab, setActiveTab] = useState<'sales' | 'charges'>('sales');
  const [saleForm, setSaleForm] = useState({ label: '', quantity: '', unit_price: '', planned_date: '', status: 'prevu' as const, notes: '' });
  const [chargeForm, setChargeForm] = useState({ label: '', annual_amount: '', monthly_pattern: 'uniform' as MonthlyPattern, notes: '' });
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalSales = sales.filter(s => s.status !== 'annule').reduce((sum, s) => sum + s.quantity * s.unit_price, 0);
  const totalChargesAnnual = charges.reduce((sum, c) => sum + c.annual_amount, 0);
  const margin = totalSales - totalChargesAnnual;

  async function addSale() {
    if (!saleForm.label || !saleForm.quantity || !saleForm.unit_price) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    await supabase.from('agri_livestock_sales').insert({ user_id: user.id, herd_id: herd.id, label: saleForm.label, quantity: parseInt(saleForm.quantity), unit_price: parseFloat(saleForm.unit_price), planned_date: saleForm.planned_date || null, campaign_year: year, status: saleForm.status, notes: saleForm.notes || null });
    setSaleForm({ label: '', quantity: '', unit_price: '', planned_date: '', status: 'prevu', notes: '' });
    setShowSaleForm(false);
    setSaving(false);
    refreshSales();
  }

  async function addCharge() {
    if (!chargeForm.label || !chargeForm.annual_amount) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }
    await supabase.from('agri_livestock_charges').insert({ user_id: user.id, herd_id: herd.id, label: chargeForm.label, annual_amount: parseFloat(chargeForm.annual_amount), monthly_pattern: chargeForm.monthly_pattern, campaign_year: year, notes: chargeForm.notes || null });
    setChargeForm({ label: '', annual_amount: '', monthly_pattern: 'uniform', notes: '' });
    setShowChargeForm(false);
    setSaving(false);
    refreshCharges();
  }

  async function deleteSale(id: string) {
    await supabase.from('agri_livestock_sales').delete().eq('id', id);
    refreshSales();
  }
  async function deleteCharge(id: string) {
    await supabase.from('agri_livestock_charges').delete().eq('id', id);
    refreshCharges();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{herd.name}</h2>
            <p className="text-sm text-gray-500">{LIVESTOCK_TYPE_LABELS[herd.livestock_type]} · {herd.total_animals} animaux · Campagne {year}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 p-4 flex-shrink-0">
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <p className="text-xs text-green-600 font-medium">Prod. vendues</p>
            <p className="text-lg font-bold text-green-900">{formatEur(totalSales)}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-3 text-center">
            <p className="text-xs text-red-600 font-medium">Charges annuelles</p>
            <p className="text-lg font-bold text-red-900">{formatEur(totalChargesAnnual)}</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${margin >= 0 ? 'bg-emerald-50' : 'bg-orange-50'}`}>
            <p className={`text-xs font-medium ${margin >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>Marge brute</p>
            <p className={`text-lg font-bold ${margin >= 0 ? 'text-emerald-900' : 'text-orange-900'}`}>{formatEur(margin)}</p>
          </div>
        </div>

        <div className="flex gap-1 px-4 flex-shrink-0">
          {(['sales', 'charges'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === t ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'}`}>
              {t === 'sales' ? 'Ventes prévues' : 'Charges annuelles'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'sales' && (
            <div className="space-y-2">
              {sales.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{s.label}</p>
                    <p className="text-xs text-gray-500">{s.quantity} têtes × {formatEur(s.unit_price)} · {s.planned_date ? new Date(s.planned_date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '—'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-green-700 text-sm">{formatEur(s.quantity * s.unit_price)}</span>
                    <button onClick={() => deleteSale(s.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {showSaleForm ? (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Libellé *</label>
                      <input type="text" value={saleForm.label} onChange={e => setSaleForm(f => ({ ...f, label: e.target.value }))} placeholder="Veaux sevrés"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Quantité *</label>
                      <input type="number" min="0" value={saleForm.quantity} onChange={e => setSaleForm(f => ({ ...f, quantity: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Prix unitaire (€) *</label>
                      <input type="number" min="0" step="10" value={saleForm.unit_price} onChange={e => setSaleForm(f => ({ ...f, unit_price: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date prévue</label>
                      <input type="date" value={saleForm.planned_date} onChange={e => setSaleForm(f => ({ ...f, planned_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowSaleForm(false)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm">Annuler</button>
                    <button onClick={addSale} disabled={saving} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">Ajouter</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowSaleForm(true)} className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-green-300 text-green-600 rounded-xl hover:bg-green-50 text-sm">
                  <Plus className="w-4 h-4" /> Ajouter une vente
                </button>
              )}
            </div>
          )}

          {activeTab === 'charges' && (
            <div className="space-y-2">
              {charges.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{c.label}</p>
                    <p className="text-xs text-gray-500">{c.monthly_pattern === 'winter_heavy' ? 'Profil hiver chargé' : 'Répartition uniforme'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-red-600 text-sm">{formatEur(c.annual_amount)}/an</span>
                    <button onClick={() => deleteCharge(c.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
              {showChargeForm ? (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-200">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Poste *</label>
                      <input type="text" value={chargeForm.label} onChange={e => setChargeForm(f => ({ ...f, label: e.target.value }))} placeholder="Alimentation, Vétérinaire..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Montant annuel (€) *</label>
                      <input type="number" min="0" step="100" value={chargeForm.annual_amount} onChange={e => setChargeForm(f => ({ ...f, annual_amount: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Profil mensuel</label>
                    <select value={chargeForm.monthly_pattern} onChange={e => setChargeForm(f => ({ ...f, monthly_pattern: e.target.value as MonthlyPattern }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm">
                      <option value="uniform">Uniforme (1/12 chaque mois)</option>
                      <option value="winter_heavy">Hiver chargé (alimentation)</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowChargeForm(false)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm">Annuler</button>
                    <button onClick={addCharge} disabled={saving} className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">Ajouter</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowChargeForm(true)} className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-green-300 text-green-600 rounded-xl hover:bg-green-50 text-sm">
                  <Plus className="w-4 h-4" /> Ajouter une charge
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function LivestockTab({ year }: Props) {
  const { herds, loading, refresh } = useAgriLivestockHerds();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AgriLivestockHerd | null>(null);
  const [detailHerd, setDetailHerd] = useState<AgriLivestockHerd | null>(null);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer ce troupeau et toutes ses données ?')) return;
    await supabase.from('agri_livestock_herds').delete().eq('id', id);
    refresh();
  }

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Élevage</h2>
          <p className="text-sm text-gray-500">{herds.length} troupeau{herds.length !== 1 ? 'x' : ''}</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium shadow-sm">
          <Plus className="w-4 h-4" />
          Nouveau troupeau
        </button>
      </div>

      {herds.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-gray-700 font-medium mb-2">Aucun troupeau enregistré</h3>
          <p className="text-gray-400 text-sm mb-6">Créez votre premier troupeau pour piloter votre activité d'élevage</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium">
            <Plus className="w-4 h-4" />
            Créer un troupeau
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {herds.map(h => (
            <div key={h.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{h.name}</h3>
                  <p className="text-sm text-gray-500">{LIVESTOCK_TYPE_LABELS[h.livestock_type]}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(h); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(h.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { label: 'Vaches', value: h.cows_count },
                  { label: 'Génisses', value: h.heifers_count },
                  { label: 'Veaux', value: h.calves_count },
                  { label: 'Autres', value: h.other_count },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-gray-800">{item.value}</p>
                    <p className="text-xs text-gray-500">{item.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 font-medium">Total : {h.total_animals} animaux</span>
                <button onClick={() => setDetailHerd(h)} className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700 font-medium">
                  <TrendingUp className="w-4 h-4" />
                  Ventes & charges {year}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showForm || editing) && (
        <HerdFormModal
          herd={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); refresh(); }}
        />
      )}
      {detailHerd && <HerdDetail herd={detailHerd} year={year} onClose={() => setDetailHerd(null)} />}
    </div>
  );
}
